import { Shield, AlertTriangle, Eye, Activity } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Security Page
 *
 * Anomaly detection and security monitoring.
 * Full implementation will be completed in Phase 5.
 */
export function SecurityPage() {
  const securityMetrics = [
    {
      title: 'Anomalies Detected',
      value: '3',
      icon: AlertTriangle,
      status: 'warning',
    },
    {
      title: 'Active Monitors',
      value: '12',
      icon: Eye,
      status: 'success',
    },
    {
      title: 'Security Events',
      value: '47',
      icon: Activity,
      status: 'info',
    },
    {
      title: 'Threat Level',
      value: 'Low',
      icon: Shield,
      status: 'success',
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Security Monitoring</h1>
        <p className="text-muted-foreground mt-2">
          Detect anomalies, monitor threats, and respond to security events
        </p>
      </div>

      {/* Security Metrics */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {securityMetrics.map(metric => {
          const Icon = metric.icon;
          return (
            <Card key={metric.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{metric.title}</CardTitle>
                <Icon
                  className={`h-4 w-4 ${
                    metric.status === 'warning'
                      ? 'text-yellow-500'
                      : metric.status === 'success'
                        ? 'text-green-500'
                        : 'text-muted-foreground'
                  }`}
                />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metric.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Anomaly Detection Area */}
      <div className="grid gap-4 lg:grid-cols-2 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Anomaly Detection</CardTitle>
            <CardDescription>AI-powered pattern recognition and threat detection</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
              <AlertTriangle className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground mb-2">Phase 5 Feature</p>
              <p className="text-xs text-muted-foreground">
                Machine learning models for detecting unusual patterns
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Security Alerts</CardTitle>
            <CardDescription>Real-time notifications for security events</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
              <Eye className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground mb-2">Phase 5 Feature</p>
              <p className="text-xs text-muted-foreground">
                Configurable alerts and automated response workflows
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Planned Features */}
      <Card>
        <CardHeader>
          <CardTitle>Security Features Roadmap</CardTitle>
          <CardDescription>
            Advanced security monitoring capabilities coming in Phase 5
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="rounded-lg border p-4">
              <h4 className="font-semibold mb-2">Anomaly Detection Engine</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Unusual access patterns detection</li>
                <li>Failed authentication attempt monitoring</li>
                <li>Suspicious activity flagging</li>
                <li>Geographic anomaly detection</li>
              </ul>
            </div>
            <div className="rounded-lg border p-4">
              <h4 className="font-semibold mb-2">Alert Management</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Configurable alert thresholds</li>
                <li>Multi-channel notifications (email, Slack, SMS)</li>
                <li>Alert prioritization and categorization</li>
                <li>Automated incident response workflows</li>
              </ul>
            </div>
            <div className="rounded-lg border p-4">
              <h4 className="font-semibold mb-2">Threat Intelligence</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>IP reputation checks</li>
                <li>User behavior analytics</li>
                <li>Threat scoring and risk assessment</li>
                <li>Integration with security information feeds</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
